import {
	Component,
	OnInit,
	Input,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
} from '@angular/core';
import { Router } from '@angular/router';
import {
	AttestationPresenceDate,
	FormationThematiqueService,
	FormateurAttestationGroup,
	FormateurStagiaireSignature,
	BackendStagiaireData,
} from '@app/models/formation-thematique/formation-thematique.service';
import { Color } from '@app/models/color';
import { SessionService } from '@app/services/session.service';
import { User } from '@app/models/user/user';
import { AttestationPresenceThematiqueService } from '@app/models/attestation-presence-thematique/attestation-presence-thematique.service';
import { SignatureRemovalRequestService } from '@app/models/signature-removal-request';
import { ErrorService } from '@app/services/error.service';
import { TranslateService } from '@ngx-translate/core';
import { NzModalService } from 'ng-zorro-antd/modal';
import { NzMessageService } from 'ng-zorro-antd/message';
import { HttpErrorResponse } from '@angular/common/http';

interface SignatureRowInterface {
	date: Date;
	period: 'afternoon' | 'morning';
	signed: boolean;
	status:
		| 'need-sign-now'
		| 'already-signed'
		| 'sign-later'
		| 'too-late-to-sign';
}

interface TransformedFormateurGroup {
	date: number;
	period: 'afternoon' | 'morning';
	stagiaires: FormateurStagiaireSignature[];
}

@Component({
	selector: 'app-atom-formation-thematique-table-signature',
	templateUrl: './atom-formation-thematique-table-signature.component.html',
	styleUrls: ['./atom-formation-thematique-table-signature.component.less'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AtomFormationThematiqueTableSignatureComponent implements OnInit {
	@Input() thematiqueId: string;
	@Input() signatureDates: AttestationPresenceDate[];
	@Input() colorThematique: Color;

	signatureRows: SignatureRowInterface[] = [];
	thematiqueEndDate: Date | null = null;
	thematiqueStartDate: Date | null = null;

	// Formateur-specific properties
	userLogged: User;
	isWithinFormateurEditableWindow = false;
	isFormateurView = false;
	formateurOwnSignatures: SignatureRowInterface[] = []; // Formateur's own signatures
	formateurGroups: TransformedFormateurGroup[] = [];
	expandedGroups = new Set<string>();

	// Modal state
	delayModalVisible = false;
	delayModalLoading = false;
	delayModalTitle = '';
	delayValue = 0;
	selectedStagiaire: FormateurStagiaireSignature;
	selectedGroup: TransformedFormateurGroup;

	// In-flight excused toggle per stagiaire+group key, to disable the button while the request runs.
	excuseInFlight = new Set<string>();

	// In-flight signature-removal proposals per attestation id to avoid double-submits.
	removalInFlight = new Set<string>();

	// Attestation ids that already have a pending removal request authored by this coach.
	// Drives the "Demande en cours" badge and hides the propose button.
	pendingRemovalAttestationIds = new Set<string>();

	constructor(
		private router: Router,
		private sessionService: SessionService,
		private formationThematiqueService: FormationThematiqueService,
		private attestationPresenceThematiqueService: AttestationPresenceThematiqueService,
		private signatureRemovalRequestService: SignatureRemovalRequestService,
		private errorService: ErrorService,
		private cdr: ChangeDetectorRef,
		private translateService: TranslateService,
		private modalService: NzModalService,
		private messageService: NzMessageService
	) {}

	// Re-interpret a timestamp's Europe/Paris wall-time as the user's local wall-time.
	// Why: slot times and thematique end_date are stored with Paris semantics; comparing
	// them in absolute time penalizes users in other timezones who expect to sign at the
	// same wall-clock moment locally.
	private parisWallTimeToLocal(input: Date | number): Date {
		const date = input instanceof Date ? input : new Date(input);
		const parts = new Intl.DateTimeFormat('en-US', {
			timeZone: 'Europe/Paris',
			year: 'numeric',
			month: '2-digit',
			day: '2-digit',
			hour: '2-digit',
			minute: '2-digit',
			second: '2-digit',
			hour12: false,
		}).formatToParts(date);
		const get = (type: string): number =>
			parseInt(parts.find((p) => p.type === type).value, 10);
		let hour = get('hour');
		if (hour === 24) hour = 0;
		return new Date(
			get('year'),
			get('month') - 1,
			get('day'),
			hour,
			get('minute'),
			get('second')
		);
	}

	async ngOnInit(): Promise<void> {
		this.userLogged = await this.sessionService.getCachedUser();
		this.isFormateurView = this.userLogged && this.userLogged.isCoach();

		// Fetch formation thematique to get start_date and end_date
		try {
			const formationThematique =
				await this.formationThematiqueService.get(this.thematiqueId);

			if (formationThematique.props.end_date) {
				this.thematiqueEndDate = this.parisWallTimeToLocal(
					formationThematique.props.end_date
				);
			}

			if (formationThematique.props.start_date) {
				this.thematiqueStartDate = this.parisWallTimeToLocal(
					formationThematique.props.start_date
				);

				// Calculate if we're within the formateur editable window
				// (between start of thematique and 1 week after start)
				const now = new Date();
				const cutoffTime = new Date(
					this.thematiqueStartDate.getTime() + 7 * 24 * 60 * 60 * 1000
				); // +1 week (7 days) from start

				this.isWithinFormateurEditableWindow =
					now >= this.thematiqueStartDate && now <= cutoffTime;
			}
		} catch (error) {
			// If we can't fetch the thematique, continue without dates
			this.errorService.handle(error);
		}

		if (this.isFormateurView) {
			await this.loadFormateurData();
		} else {
			this.prepareSignatureRows();
		}

		this.cdr.markForCheck();
	}

	prepareSignatureRows(): void {
		const now = new Date();

		this.signatureRows = this.signatureDates.map((signatureDate) => {
			const signatureDateTime = this.parisWallTimeToLocal(
				new Date(signatureDate.signatureDateTime)
			);

			let status:
				| 'need-sign-now'
				| 'already-signed'
				| 'sign-later'
				| 'too-late-to-sign';

			if (signatureDate.signed) {
				status = 'already-signed';
			} else if (signatureDate.period === 'afternoon') {
				// Afternoon: Allow signing 30 minutes before, until 30 minutes after thematique end
				const earlyAccessTime = new Date(
					signatureDateTime.getTime() - 30 * 60 * 1000
				); // 30 minutes before
				const canSignNow = now >= earlyAccessTime;

				// Allow signing up to 30 minutes after thematique end
				const extendedEndTime = this.thematiqueEndDate
					? new Date(
							this.thematiqueEndDate.getTime() + 30 * 60 * 1000
					  )
					: null;
				const tooLateToSign = extendedEndTime && now >= extendedEndTime;

				if (canSignNow && !tooLateToSign) {
					status = 'need-sign-now';
				} else if (tooLateToSign) {
					status = 'too-late-to-sign';
				} else {
					status = 'sign-later'; // Too early
				}
			} else {
				// Morning: Can sign from signatureDateTime until afternoon of same day (or thematique end)
				const canSignNow = now >= signatureDateTime;

				// Find afternoon session of the same day (if exists)
				const afternoonCutoff =
					this.findAfternoonSessionTime(signatureDateTime);
				const signEndTime = afternoonCutoff
					? this.thematiqueEndDate &&
					  afternoonCutoff > this.thematiqueEndDate
						? this.thematiqueEndDate
						: afternoonCutoff
					: this.thematiqueEndDate;

				const tooLateToSign = signEndTime && now >= signEndTime;

				if (canSignNow && !tooLateToSign) {
					status = 'need-sign-now';
				} else if (tooLateToSign) {
					status = 'too-late-to-sign';
				} else {
					status = 'sign-later'; // Too early
				}
			}

			return {
				date: signatureDateTime,
				period: signatureDate.period,
				signed: signatureDate.signed,
				status,
			};
		});

		// Sort by date ascending
		this.signatureRows.sort((a, b) => a.date.getTime() - b.date.getTime());
	}

	private findAfternoonSessionTime(morningDate: Date): Date | null {
		// Find afternoon session on the same day
		const sameDayAfternoon = this.signatureDates.find((sd) => {
			const sdDate = this.parisWallTimeToLocal(
				new Date(sd.signatureDateTime)
			);
			return (
				sd.period === 'afternoon' &&
				sdDate.getFullYear() === morningDate.getFullYear() &&
				sdDate.getMonth() === morningDate.getMonth() &&
				sdDate.getDate() === morningDate.getDate()
			);
		});

		return sameDayAfternoon
			? this.parisWallTimeToLocal(
					new Date(sameDayAfternoon.signatureDateTime)
			  )
			: null;
	}

	private findAfternoonSessionTimeFormateur(
		morningDate: Date,
		signatureDates: AttestationPresenceDate[]
	): Date | null {
		// Find afternoon session on the same day for formateur
		const sameDayAfternoon = signatureDates.find((sd) => {
			const sdDate = this.parisWallTimeToLocal(
				new Date(sd.signatureDateTime)
			);
			return (
				sd.period === 'afternoon' &&
				sdDate.getFullYear() === morningDate.getFullYear() &&
				sdDate.getMonth() === morningDate.getMonth() &&
				sdDate.getDate() === morningDate.getDate()
			);
		});

		return sameDayAfternoon
			? this.parisWallTimeToLocal(
					new Date(sameDayAfternoon.signatureDateTime)
			  )
			: null;
	}

	trackByDate = (_: number, item: SignatureRowInterface): string => {
		return `${item.date.toISOString()}-${item.period}`;
	};

	navigateToSignature(row: SignatureRowInterface): void {
		if (row.status === 'need-sign-now') {
			const timestamp = row.date.getTime();
			const periodFr = this.periodToFrench(row.period);
			this.router.navigate([
				'/session',
				this.thematiqueId,
				'presence',
				timestamp,
				periodFr,
			]);
		}
	}

	private periodToFrench(period: 'morning' | 'afternoon'): string {
		return period === 'morning' ? 'matin' : 'apres-midi';
	}

	getPeriodLabel(period: 'afternoon' | 'morning'): string {
		return period === 'morning' ? 'Matin' : 'Après-midi';
	}

	getMissingSignaturesCount(group: TransformedFormateurGroup): number {
		// Q1.3: excused stagiaires are neutralized — they don't count as missing.
		return group.stagiaires.filter((s) => !s.signed && !s.excused).length;
	}

	getExcusedCount(group: TransformedFormateurGroup): number {
		return group.stagiaires.filter((s) => s.excused).length;
	}

	// Formateur view methods
	async loadFormateurData(): Promise<void> {
		try {
			// Load both formateur's own signatures and stagiaire signatures in parallel
			const [formateurMetadata, ownMetadata, pendingAttestationIds] =
				await Promise.all([
					this.formationThematiqueService.getFormateurMetadata(
						this.thematiqueId
					),
					this.formationThematiqueService.getMetadata(
						this.thematiqueId
					),
					this.signatureRemovalRequestService
						.listPendingAttestationIds(this.thematiqueId)
						.catch(() => new Set<string>()),
				]);
			this.pendingRemovalAttestationIds = pendingAttestationIds;

			// Process formateur's own signatures (like respondant view)
			const now = new Date();
			this.formateurOwnSignatures = (
				ownMetadata.attestation_presence_dates || []
			).map((signatureDate) => {
				const signatureDateTime = this.parisWallTimeToLocal(
					new Date(signatureDate.signatureDateTime)
				);

				let status:
					| 'need-sign-now'
					| 'already-signed'
					| 'sign-later'
					| 'too-late-to-sign';

				if (signatureDate.signed) {
					status = 'already-signed';
				} else {
					// For formateur: can sign from signatureDateTime until 1 week after the signature day
					const canSignNow = now >= signatureDateTime;

					// Calculate end of day + 1 week for cutoff
					const signatureDay = new Date(signatureDateTime);
					signatureDay.setHours(23, 59, 59, 999); // End of signature day
					const cutoffTime = new Date(
						signatureDay.getTime() + 7 * 24 * 60 * 60 * 1000
					); // +1 week (7 days)

					const tooLateToSign = now > cutoffTime;

					if (canSignNow && !tooLateToSign) {
						status = 'need-sign-now';
					} else if (tooLateToSign) {
						status = 'too-late-to-sign';
					} else {
						status = 'sign-later'; // Too early
					}
				}

				return {
					date: signatureDateTime,
					period: signatureDate.period,
					signed: signatureDate.signed,
					status,
				};
			});

			// Sort by date ascending
			this.formateurOwnSignatures.sort(
				(a, b) => a.date.getTime() - b.date.getTime()
			);

			// Transform backend data for stagiaire signatures
			this.formateurGroups = (
				formateurMetadata.attestation_presence_dates || []
			).map((group) => {
				const signatureDate = this.parisWallTimeToLocal(
					new Date(group.signatureDateTime)
				);
				const timestamp = signatureDate.getTime();

				// Transform each stagiaire (filter out test accounts)
				const testEmailRegex = /^technique\+.+@nove-concept\.com$/i;
				const transformedStagiaires: FormateurStagiaireSignature[] =
					group.stagiaires
						.filter(
							(stagiaireData: BackendStagiaireData) =>
								!stagiaireData.stagiaire.email ||
								!testEmailRegex.test(
									stagiaireData.stagiaire.email
								)
						)
						.map((stagiaireData: BackendStagiaireData) => {
							const att = stagiaireData.attestation;
							const isExcused = !!(att && att.excused);
							return {
								stagiaire_id: stagiaireData.stagiaire._id,
								stagiaire_name: `${stagiaireData.stagiaire.first_name} ${stagiaireData.stagiaire.last_name}`,
								signed: !!att && !isExcused,
								excused: isExcused,
								signed_at: att?.created_at,
								delay: att?.delay,
								attestation_id: att?._id,
							};
						});

				return {
					date: timestamp,
					period: group.period,
					stagiaires: transformedStagiaires,
				};
			});

			// Sort groups by date ascending
			this.formateurGroups.sort((a, b) => a.date - b.date);

			// Trigger change detection for OnPush strategy
			this.cdr.markForCheck();
		} catch (err) {
			this.errorService.handle(err);
			this.formateurGroups = [];
			this.formateurOwnSignatures = [];
			this.cdr.markForCheck();
		}
	}

	getGroupKey(group: TransformedFormateurGroup): string {
		return `${group.date}-${group.period}`;
	}

	isGroupExpanded(group: TransformedFormateurGroup): boolean {
		return this.expandedGroups.has(this.getGroupKey(group));
	}

	toggleGroup(group: TransformedFormateurGroup): void {
		const key = this.getGroupKey(group);
		if (this.expandedGroups.has(key)) {
			this.expandedGroups.delete(key);
		} else {
			this.expandedGroups.add(key);
		}
		this.cdr.markForCheck();
	}

	trackByGroup = (_: number, group: TransformedFormateurGroup): string => {
		return this.getGroupKey(group);
	};

	trackByStagiaire = (
		_: number,
		stagiaire: FormateurStagiaireSignature
	): string => {
		return stagiaire.stagiaire_id;
	};

	// Modal methods
	openDelayModal(
		stagiaire: FormateurStagiaireSignature,
		group: TransformedFormateurGroup
	): void {
		this.selectedStagiaire = stagiaire;
		this.selectedGroup = group;

		if (stagiaire.signed) {
			// Update existing delay
			this.delayModalTitle = this.translateService.instant(
				'presence-signature_modal-edit-absence-title'
			);
			this.delayValue = stagiaire.delay || 0;
		} else {
			// Sign unsigned attestation
			this.delayModalTitle = this.translateService.instant(
				'presence-signature_modal-sign-title'
			);
			this.delayValue = 0;
		}

		this.delayModalVisible = true;
	}

	closeDelayModal(): void {
		this.delayModalVisible = false;
		this.delayModalLoading = false;
		this.selectedStagiaire = null;
		this.selectedGroup = null;
		this.delayValue = 0;
	}

	getExcuseKey(
		stagiaire: FormateurStagiaireSignature,
		group: TransformedFormateurGroup
	): string {
		return `${this.getGroupKey(group)}-${stagiaire.stagiaire_id}`;
	}

	isExcuseInFlight(
		stagiaire: FormateurStagiaireSignature,
		group: TransformedFormateurGroup
	): boolean {
		return this.excuseInFlight.has(this.getExcuseKey(stagiaire, group));
	}

	async toggleExcused(
		stagiaire: FormateurStagiaireSignature,
		group: TransformedFormateurGroup
	): Promise<void> {
		// API upserts/toggles based on existing state — same call for "mark" and "unmark".
		const key = this.getExcuseKey(stagiaire, group);
		if (this.excuseInFlight.has(key)) return;
		this.excuseInFlight.add(key);
		this.cdr.markForCheck();
		try {
			await this.attestationPresenceThematiqueService.markExcused(
				this.thematiqueId,
				group.date,
				group.period,
				stagiaire.stagiaire_id
			);
			await this.loadFormateurData();
		} catch (err) {
			this.errorService.handle(err);
		} finally {
			this.excuseInFlight.delete(key);
			this.cdr.markForCheck();
		}
	}

	isRemovalInFlight(attestationId: string): boolean {
		return !!attestationId && this.removalInFlight.has(attestationId);
	}

	hasPendingRemoval(attestationId: string): boolean {
		return (
			!!attestationId &&
			this.pendingRemovalAttestationIds.has(attestationId)
		);
	}

	proposeSignatureRemoval(attestationId: string): void {
		if (!attestationId || this.removalInFlight.has(attestationId)) {
			return;
		}

		this.modalService.confirm({
			nzTitle: this.translateService.instant(
				'signature-removal-request_confirm-title'
			),
			nzContent: this.translateService.instant(
				'signature-removal-request_confirm-content'
			),
			nzOkText: this.translateService.instant('common_yes'),
			nzCancelText: this.translateService.instant('common_no'),
			nzOnOk: () => this.submitSignatureRemoval(attestationId),
		});
	}

	private async submitSignatureRemoval(attestationId: string): Promise<void> {
		this.removalInFlight.add(attestationId);
		this.cdr.markForCheck();
		try {
			await this.signatureRemovalRequestService.proposeRemoval(
				attestationId
			);
			this.pendingRemovalAttestationIds.add(attestationId);
			this.messageService.success(
				this.translateService.instant(
					'signature-removal-request_create-success'
				)
			);
		} catch (err) {
			this.handleProposalError(err);
		} finally {
			this.removalInFlight.delete(attestationId);
			this.cdr.markForCheck();
		}
	}

	private handleProposalError(err: any): void {
		if (err instanceof HttpErrorResponse) {
			if (err.status === 409) {
				this.messageService.error(
					this.translateService.instant(
						'signature-removal-request_error-already-pending'
					)
				);
				return;
			}
			if (err.status === 403) {
				this.messageService.error(
					this.translateService.instant(
						'signature-removal-request_error-not-formateur'
					)
				);
				return;
			}
		}
		this.errorService.handle(err);
	}

	async submitDelayModal(): Promise<void> {
		try {
			this.delayModalLoading = true;

			if (this.selectedStagiaire.signed) {
				// Update delay for signed attestation using default update method
				await this.attestationPresenceThematiqueService.update(
					this.selectedStagiaire.attestation_id,
					{ delay: this.delayValue }
				);
			} else {
				// Create attestation for unsigned stagiaire using public endpoint
				await this.attestationPresenceThematiqueService.signDelay(
					this.thematiqueId,
					this.selectedGroup.date,
					this.selectedGroup.period,
					this.selectedStagiaire.stagiaire_id,
					this.delayValue
				);
			}

			// Reload formateur data
			await this.loadFormateurData();
			this.closeDelayModal();
			this.cdr.markForCheck();
		} catch (err) {
			this.delayModalLoading = false;
			this.errorService.handle(err);
		}
	}
}
